// Formato, paleta y títulos de conclusión del tablero de economía del embudo
// (#1291, v2 #1352). Puras: sin React, para probarlas solas.
import type { BucketId, EconomicsReport, StageClass } from "../../services/economicsService";

export function money(value: number | null | undefined, opts: { compact?: boolean; decimals?: number } = {}): string {
  if (value == null || !Number.isFinite(value)) return "—";
  if (opts.compact) {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)} M`;
    if (abs >= 10_000) return `$${Math.round(value / 1000)} k`;
  }
  return value.toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: opts.decimals ?? 0 });
}

export function pct(value: number | null | undefined, decimals = 0): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(decimals)} %`;
}

export function num(value: number | null | undefined, decimals = 0): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("es-MX", { maximumFractionDigits: decimals });
}

export function days(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${num(value, 1)} d`;
}

const MONTHS_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** "2026-03" → "mar 26". */
export function monthLabel(period: string): string {
  const [y, m] = period.split("-");
  const idx = Number(m) - 1;
  return `${MONTHS_ES[idx] ?? m} ${String(y).slice(2)}`;
}

export function currentPeriod(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export function shiftPeriod(period: string, delta: number): string {
  const [y, m] = period.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export type RangePreset = "3m" | "6m" | "12m" | "ytd" | "custom";

export function presetRange(preset: Exclude<RangePreset, "custom">, now = new Date()): { from: string; to: string } {
  const to = currentPeriod(now);
  if (preset === "ytd") return { from: `${now.getFullYear()}-01`, to };
  const months = preset === "3m" ? 3 : preset === "6m" ? 6 : 12;
  return { from: shiftPeriod(to, -(months - 1)), to };
}

export function fileSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return "—";
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const min = Math.round((now - t) / 60000);
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
}

// ---------------------------------------------------------------------------
// Paleta. El color sigue a la entidad, nunca a su posición: una bolsa de costo
// tiene siempre el mismo color en todas las gráficas. Validada (separación para
// daltonismo y contraste, claro y oscuro); el gris es deliberado para «Otros».
// ---------------------------------------------------------------------------

export const BUCKET_ORDER: BucketId[] = ["ads", "platforms", "marketingTeam", "salesTeam", "otherMarketing"];

export const BUCKET_META: Record<BucketId, { label: string; short: string; color: string }> = {
  ads: { label: "Publicidad (pauta)", short: "Publicidad", color: "#1f6fd1" },
  platforms: { label: "Plataformas de marketing", short: "Plataformas", color: "#d9722a" },
  marketingTeam: { label: "Equipo de marketing", short: "Marketing", color: "#12998c" },
  salesTeam: { label: "Equipo de ventas", short: "Ventas", color: "#7b57e0" },
  otherMarketing: { label: "Otros gastos de marketing", short: "Otros", color: "#6b7280" },
};

/** Resultado de un negocio: estados, con color reservado (no se reutiliza para series). */
export const OUTCOME_META: Record<StageClass, { label: string; color: string; chip: string }> = {
  won: { label: "Ganado", color: "#059669", chip: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  open: { label: "Activo", color: "#1d4ed8", chip: "bg-blue-500/15 text-blue-700 dark:text-blue-300" },
  parked: { label: "En pausa", color: "#a16207", chip: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  lost: { label: "Perdido", color: "#dc2626", chip: "bg-red-500/15 text-red-600 dark:text-red-400" },
};

export const STAGE_CLASS_OPTIONS: { value: StageClass; label: string }[] = [
  { value: "open", label: "Abierta" },
  { value: "won", label: "Ganada" },
  { value: "lost", label: "Perdida" },
  { value: "parked", label: "En pausa" },
];

export const PALETTE = {
  ink: "#0f172a",
  funnel: "#1e3a5f",
  funnelLight: "#94a3b8",
  accent: "#d9722a",
  grid: { light: "#e5e7eb", dark: "#374151" },
  tick: { light: "#6b7280", dark: "#9ca3af" },
};

export const SEVERITY_TONES: Record<"high" | "medium" | "low" | "info", { chip: string; bar: string; icon: string; label: string }> = {
  high: { chip: "bg-red-500/15 text-red-600 dark:text-red-400", bar: "bg-red-500", icon: "ri-alarm-warning-line", label: "Prioridad alta" },
  medium: { chip: "bg-amber-500/15 text-amber-700 dark:text-amber-400", bar: "bg-amber-500", icon: "ri-error-warning-line", label: "Prioridad media" },
  low: { chip: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-500", icon: "ri-lightbulb-line", label: "Oportunidad" },
  info: { chip: "bg-sky-500/15 text-sky-600 dark:text-sky-400", bar: "bg-sky-500", icon: "ri-information-line", label: "Dato" },
};

export const QUALITY_TONES: Record<"ok" | "warn" | "bad", { dot: string; label: string }> = {
  ok: { dot: "bg-emerald-500", label: "Bien" },
  warn: { dot: "bg-amber-500", label: "Revisar" },
  bad: { dot: "bg-red-500", label: "Falta" },
};

// ---------------------------------------------------------------------------
// Títulos de conclusión: cada exhibit dice lo que el dato significa, no cómo
// se llama la gráfica.
// ---------------------------------------------------------------------------

export function titles(r: EconomicsReport) {
  const k = r.kpis;
  const biggest = [...r.costs.buckets].sort((a, b) => b.amount - a.amount)[0];
  const dead = r.allocation.filter((a) => a.kind === "lost" || a.kind === "parked").reduce((a, x) => a + x.share, 0);
  const open = r.funnel.stages.filter((s) => s.kind === "open");
  const first = open[0];
  const worstLoss = [...open].sort((a, b) => b.lostHere - a.lostHere)[0];
  const lastMonths = r.cohorts.filter((c) => c.cost > 0 || c.leads > 0 || c.deals > 0);
  const busiest = [...r.pipelines].sort((a, b) => b.deals - b.untouched - (a.deals - a.untouched))[0];
  return {
    investment:
      r.costs.total > 0 && biggest
        ? `${biggest.label} concentra ${pct(biggest.share)} de los ${money(r.costs.total)} de inversión comercial`
        : "No hay inversión comercial registrada en el periodo",
    unitCosts: k.costPerLead != null ? `Cada lead cuesta ${money(k.costPerLead)}${k.costPerDemo != null ? ` y cada demo ${money(k.costPerDemo)}` : ""}${k.cac != null ? `; cada cliente nuevo, ${money(k.cac)}` : ""}` : "Sin leads en el periodo no hay costo unitario que medir",
    funnel:
      k.deals > 0
        ? `De ${num(k.deals)} negocios, ${k.proposals != null ? `${num(k.proposals)} llegaron a propuesta y ` : ""}${num(k.won)} se ${k.won === 1 ? "ganó" : "ganaron"}${first && first.current && first.current / k.deals >= 0.3 ? `; ${pct(first.current / k.deals)} sigue sin contactar` : ""}`
        : "No se abrieron negocios en el periodo",
    leakage:
      k.deals > 0
        ? `${pct(dead)} de la inversión está en negocios perdidos o en pausa${worstLoss && worstLoss.lostHere > 0 ? `; la mayoría se pierde en «${worstLoss.label}»` : ""}`
        : "Sin negocios no hay fugas que medir",
    activity: k.demos > 0 ? `${num(k.demos)} demos en el periodo: cada una costó ${money(k.costPerDemo)}` : "No hay demos registradas en el periodo",
    cohorts: lastMonths.length ? `${monthLabel(lastMonths[lastMonths.length - 1].period)}: ${num(lastMonths[lastMonths.length - 1].leads)} leads, ${num(lastMonths[lastMonths.length - 1].demos)} demos y ${num(lastMonths[lastMonths.length - 1].deals)} negocios nuevos` : "Sin actividad por mes en el periodo",
    pipelines: busiest ? `«${busiest.label}» concentra el trabajo comercial: ${num(busiest.deals - busiest.untouched)} de sus ${num(busiest.deals)} negocios ya se movieron` : "Un solo pipeline en el análisis",
    breakEven:
      r.breakEven.winsPerMonth != null
        ? `Se necesitan ${num(r.breakEven.winsPerMonth)} ${r.breakEven.winsPerMonth === 1 ? "cierre" : "cierres"} al mes para pagar el costo comercial de ${money(r.breakEven.monthlyCost)}`
        : r.breakEven.monthlyCost
          ? `El costo comercial es de ${money(r.breakEven.monthlyCost)} al mes; sin montos en los negocios no se sabe cuántos cierres lo pagan`
          : "Sin costo comercial registrado no hay punto de equilibrio",
  };
}
